import { ArrowButtonDownBold } from '@ha/icons';
import React, { useState, useCallback } from 'react';
import { makeStyles } from '@material-ui/core';

import {
  Button,
  ButtonProps,
  Menu,
  MenuItem,
  Typography,
  units,
} from '@ha/dsl';

import { useIntl } from 'ha/i18n';
import { SortingValue } from 'ha/types/SearchFilters';

import useSorting from '../hooks/useSorting';

interface Props {
  size?: ButtonProps['size'];
  disabled?: boolean;
  isMobile?: boolean;
}

const useStyles = makeStyles(theme => ({
  button: {
    height: units.rem(2.5),
    minWidth: '126px',
    borderColor: theme.palette.primary.main,
  },
  label: {
    justifyContent: 'space-between',
  },
  icon: {
    paddingRight: '2px',
  },
}));

const SortingButton: React.FC<Props> = ({ size, disabled, isMobile }) => {
  const classes = useStyles();
  const { T } = useIntl();
  const { currentSorting, onChange, onOpen, options } = useSorting();

  const [anchorEl, setAnchorEl] = useState<Element | undefined>(undefined);
  const [isPristine, setPristine] = useState(
    currentSorting === SortingValue.BestMatch,
  );

  const handleOpen = useCallback(
    (event: React.MouseEvent) => {
      onOpen();
      setAnchorEl(event.currentTarget);
    },
    [onOpen],
  );

  const handleClose = useCallback(() => {
    setAnchorEl(undefined);
  }, [setAnchorEl]);

  const handleMenuItemClick = useCallback(
    (value: SortingValue) => {
      setPristine(false);
      onChange(value);
      setAnchorEl(undefined);
    },
    [onChange],
  );

  return (
    <React.Fragment>
      <Button
        variant={isPristine ? 'outlined' : 'contained'}
        data-test-locator="Filters sorting Button"
        size={size}
        color="primary"
        onClick={handleOpen}
        iconSize="inherit"
        endIcon={<ArrowButtonDownBold />}
        role="button"
        disabled={disabled}
        className={isMobile ? classes.button : undefined}
        {...(isMobile
          ? {
              classes: {
                label: classes.label,
                endIcon: classes.icon,
              },
            }
          : {})}
      >
        {isPristine
          ? T('Sort by')
          : options.find(option => option.value === currentSorting)?.key}
      </Button>
      <Menu
        id="sorting-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem dense disabled>
          <Typography.Caption textTransform="uppercase">
            {T('Sort by')}
          </Typography.Caption>
        </MenuItem>
        {options.map(option => (
          <MenuItem
            dense
            key={option.key}
            selected={option.value === currentSorting}
            onClick={() => handleMenuItemClick(option.value)}
          >
            {option.key}
          </MenuItem>
        ))}
      </Menu>
    </React.Fragment>
  );
};

export default SortingButton;
